import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { dashboardApi } from '@/api/dashboard';
import { useAuthStore } from '@/store/auth';
import { useNotificationsStore } from '@/store/notifications';

/**
 * Polls the dashboard summary and converts the alert-worthy figures into
 * persistent notifications. Each fact uses a stable `dedupeKey` so re-polling
 * refreshes the same row rather than stacking new copies.
 *
 * Mounted once at AppLayout level. Returns nothing.
 */
export function useDashboardNotifications() {
  const token = useAuthStore((s) => s.token);
  const push = useNotificationsStore((s) => s.push);

  const { data } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: dashboardApi.summary,
    enabled: !!token,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  useEffect(() => {
    if (!data) return;

    if (data.fees.defaulterCount > 0) {
      push({
        type: 'warning',
        title: `${data.fees.defaulterCount} fee defaulter${data.fees.defaulterCount > 1 ? 's' : ''}`,
        description: `Outstanding PKR ${Number(data.fees.outstanding).toLocaleString('en-PK')}`,
        link: '/fee/defaulters',
        dedupeKey: 'fee-defaulters',
      });
    }

    if (data.inventory.lowStockCount > 0) {
      push({
        type: 'warning',
        title: `${data.inventory.lowStockCount} item${data.inventory.lowStockCount > 1 ? 's' : ''} below reorder level`,
        description: 'Review inventory and re-order soon.',
        link: '/reports/low-stock',
        dedupeKey: 'inventory-low-stock',
      });
    }

    if (data.frontDesk.openComplaints > 0) {
      push({
        type: 'info',
        title: `${data.frontDesk.openComplaints} open complaint${data.frontDesk.openComplaints > 1 ? 's' : ''}`,
        description: 'Front desk needs attention.',
        link: '/frontdesk/complaints',
        dedupeKey: 'frontdesk-complaints',
      });
    }

    if (data.academics.upcomingExamsThisWeek > 0) {
      push({
        type: 'info',
        title: `${data.academics.upcomingExamsThisWeek} exam${data.academics.upcomingExamsThisWeek > 1 ? 's' : ''} this week`,
        description: 'Check the exam schedule.',
        link: '/academics/exam-schedule',
        dedupeKey: 'academics-upcoming-exams',
      });
    }

    for (const notice of data.recentNotices.slice(0, 3)) {
      push({
        type: 'info',
        title: notice.title,
        description: `Notice · ${notice.category}`,
        link: '/frontdesk/notices',
        dedupeKey: `notice-${notice.id}`,
      });
    }
  }, [data, push]);
}
